import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  type Entity,
  type IdentityEntityGetEntitiesData,
  identityEntityDeleteEntity,
  identityEntityGetEntitiesOptions,
} from '@column/api';
import { Button, Dropdown, Icon, ObjectCard, Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { FilterDropdown } from '~/app/pages/Transfers/Overview';
import { ROUTE } from '~/app/routes';
import { EmptyState, Loading, PageHeader } from '~/components';
import { ButtonGroupDropdown } from '~/components/ButtonGroupDropdown';
import { useDialog } from '~/components/Dialog/Provider';
import { Search } from '~/components/Search';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { EntityRepository, EntityWithDetails } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { ApiError } from '~/typings/API';
import { ApiErrCode } from '~/typings/enum';
import { formatString } from '~/util';

import { EntityFiltersModal } from './_components/EntityFiltersModal';

type EntityType = '' | 'PERSON' | 'BUSINESS';
type VerificationStatusFilter = '' | 'UNVERIFIED' | 'PENDING' | 'MANUAL_REVIEW' | 'VERIFIED' | 'DENIED';

type EntityListQuery = NonNullable<IdentityEntityGetEntitiesData['query']>;

interface EntityFilters {
  type: EntityType;
  verificationStatus: VerificationStatusFilter;
  query: string;
}

const EMPTY_FILTERS: EntityFilters = {
  type: '',
  verificationStatus: '',
  query: '',
};

const isEntityType = (value: string): value is EntityType => value === '' || value === 'PERSON' || value === 'BUSINESS';

const isVerificationStatusFilter = (value: string): value is VerificationStatusFilter =>
  value === '' ||
  value === 'UNVERIFIED' ||
  value === 'PENDING' ||
  value === 'MANUAL_REVIEW' ||
  value === 'VERIFIED' ||
  value === 'DENIED';

const filtersFromSearchParams = (params: URLSearchParams, searchEnabled: boolean): EntityFilters => {
  const typeRaw = params.get('type') ?? '';
  const statusRaw = params.get('verification_status') ?? '';
  return {
    type: isEntityType(typeRaw) ? typeRaw : '',
    verificationStatus: isVerificationStatusFilter(statusRaw) ? statusRaw : '',
    query: searchEnabled ? (params.get('query') ?? '') : '',
  };
};

const filtersToSearchParams = (filters: EntityFilters, searchEnabled: boolean): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.type) params.set('type', filters.type);
  if (filters.verificationStatus) params.set('verification_status', filters.verificationStatus);
  if (searchEnabled && filters.query) params.set('query', filters.query);
  return params;
};

const TYPE_OPTIONS: { label: string; value: EntityType }[] = [
  { label: 'Person', value: 'PERSON' },
  { label: 'Business', value: 'BUSINESS' },
];

const VERIFICATION_STATUS_OPTIONS: { label: string; value: VerificationStatusFilter }[] = [
  { label: 'Unverified', value: 'UNVERIFIED' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Manual review', value: 'MANUAL_REVIEW' },
  { label: 'Verified', value: 'VERIFIED' },
  { label: 'Denied', value: 'DENIED' },
];

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 0 var(--page-margin) 12px;
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ResetFilter = styled(Button)`
  padding-left: 0;
  padding-right: 0;
`;

const CompactFilterDropdown = styled(FilterDropdown)`
  min-width: 0;
`;

const EntitySearch = styled(Search)`
  min-width: 216px;
  flex: 0 0 216px;

  form,
  label {
    width: 100%;
  }
`;

const StyledDropdown = styled(Dropdown)`
  margin: -10px 0;
`;

const RootEntityCardWrapper = styled(Inner)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  margin-top: 2px;
  justify-content: space-between;
`;

const RootEntityLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};
`;

const RootEntityCard = styled.div`
  flex-grow: 1;
`;

const getEntityName = (entity?: EntityWithDetails) => {
  if (!entity) return undefined;
  if (entity.personDetails)
    return `${entity.personDetails.firstName || ''} ${entity.personDetails.lastName || ''}`.trim();
  if (entity.businessDetails) return entity.businessDetails.businessName;
  return undefined;
};

const getEntityIcon = (entity?: EntityWithDetails): keyof typeof Icon | undefined => {
  if (!entity) return undefined;
  return entity.type === 'PERSON' ? 'User' : 'Business';
};

const buildQuery = (
  filters: EntityFilters,
  searchEnabled: boolean,
  pageSize: number,
  cursor?: string
): EntityListQuery => {
  const query: EntityListQuery = {
    limit: pageSize,
    is_root: false,
  };
  if (cursor) query.starting_after = cursor;
  if (filters.type) query.type = filters.type;
  if (filters.verificationStatus) query.verification_status = filters.verificationStatus;
  if (searchEnabled && filters.query) query.query = filters.query;
  return query;
};

export const PageEntities: React.FC = () => {
  const { currentUser, currentPlatform, isSandbox } = useSessionStore();
  const { openAlert } = useDialog();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const mode: 'sandbox' | 'live' = isSandbox ? 'sandbox' : 'live';

  const isEntitySearchEnabled = useFeatureFlag(FeatureFlag.EnableEntitySearch);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialPagination = useRef({
    cursor: searchParams.get('starting_after') ?? undefined,
    pageIndex: Number(searchParams.get('page_index')) || 0,
  });
  const {
    cursor,
    onNext,
    onPrevious,
    reset: resetPagination,
    setPageSize,
    pageIndex,
    pageSize,
    hasPrevious,
  } = usePaginationState({
    initialCursor: initialPagination.current.cursor,
    initialPageIndex: initialPagination.current.pageIndex,
  });

  const [filters, setFilters] = useState<EntityFilters>(() =>
    filtersFromSearchParams(searchParams, isEntitySearchEnabled)
  );
  const [queryInput, setQueryInput] = useState(
    () => filtersFromSearchParams(searchParams, isEntitySearchEnabled).query
  );
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSmallViewport, setIsSmallViewport] = useState(false);

  useEffect(() => {
    const params = filtersToSearchParams(filters, isEntitySearchEnabled);
    if (cursor) params.set('starting_after', cursor);
    if (pageIndex > 0) params.set('page_index', String(pageIndex));
    setSearchParams(params, { replace: true });
  }, [filters, isEntitySearchEnabled, cursor, pageIndex, setSearchParams]);

  const updateFilter = <K extends keyof EntityFilters>(key: K, value: EntityFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const queryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPendingQuery = useCallback(() => {
    if (queryDebounceRef.current) {
      clearTimeout(queryDebounceRef.current);
      queryDebounceRef.current = null;
    }
  }, []);
  useEffect(() => cancelPendingQuery, [cancelPendingQuery]);

  const handleQueryInputChange = (value: string) => {
    setQueryInput(value);
    cancelPendingQuery();
    queryDebounceRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, query: value }));
    }, 300);
  };

  const handleFilterReset = () => {
    cancelPendingQuery();
    setFilters({ ...EMPTY_FILTERS });
    setQueryInput('');
  };

  const isFilterActive = Boolean(filters.type) || Boolean(filters.verificationStatus) || Boolean(filters.query);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      // Without a sort dropdown competing for toolbar space the filters fit
      // inline comfortably, so only collapse to the modal on phone-sized viewports.
      const threshold = isFilterActive ? 720 : 640;
      setIsSmallViewport(window.innerWidth < threshold);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFilterActive]);

  const query = useMemo(
    () => buildQuery(filters, isEntitySearchEnabled, pageSize, cursor),
    [filters, isEntitySearchEnabled, pageSize, cursor]
  );

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [filters, resetPagination]);

  const {
    data: rootEntity,
    isLoading: isRootEntityLoading,
    error: rootEntityError,
  } = useQuery<EntityWithDetails | null>({
    queryKey: [currentPlatform?.id, 'platform', 'root-entity', isSandbox],
    queryFn: () => EntityRepository.getRoot(currentPlatform?.id ?? '', mode),
    enabled: !!currentPlatform?.id,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount: number, error: unknown) => {
      const apiError = error as Partial<ApiError> | undefined;
      if (apiError?.code === ApiErrCode.ResourceNotFound.toString()) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const {
    data: entitiesData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    ...identityEntityGetEntitiesOptions({ query }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });

  const entities = useMemo<Entity[]>(() => entitiesData?.entities ?? [], [entitiesData]);
  const hasMore = entitiesData?.has_more ?? false;

  const handleDelete = (entry: Entity) => {
    openAlert({
      headline: 'Delete entity',
      content: 'This action cannot be undone.',
      confirmButton: {
        label: 'Delete',
        variant: 'danger',
      },
      onSuccess: () => {
        if (!currentUser) {
          return;
        }
        identityEntityDeleteEntity({ path: { entity_id: entry.id }, throwOnError: true })
          .then(() => {
            addSuccessNotification({
              content: entry.name ? `${entry.name} deleted` : 'Entity deleted',
            });
            refetch();
          })
          .catch((error) => {
            addDangerNotification({ content: error?.message ?? 'Failed to delete entity' });
          });
      },
    });
  };

  const columns: TableColumn<Entity>[] = [
    {
      id: 'name',
      header: 'Name',
      width: { min: 200, max: '4fr' },
      cell: ({ row }) => <Table.TextCell value={row.name ?? ''} weight="medium" />,
    },
    {
      id: 'type',
      header: 'Type',
      width: { min: 'min-content', max: '1fr' },
      cell: ({ row }) => (
        <Table.ChipCell
          value={formatString(row.type ?? '')}
          type="info"
          icon={row.type === 'BUSINESS' ? <Icon.Business /> : <Icon.User />}
        />
      ),
    },
    {
      id: 'verification_status',
      header: 'Status',
      width: { min: 'min-content', max: '1fr' },
      cell: ({ row }) => {
        const value = row.verification_status;
        if (value === 'VERIFICATION_STATUS_NONE') {
          return <Table.ChipCell value="None" />;
        }
        if (value === 'UNVERIFIED') {
          return <Table.ChipCell value="Unverified" type="warning" icon={<Icon.CircleQuestionmark />} />;
        }
        if (value === 'PENDING') {
          return <Table.ChipCell value="Pending" icon={<Icon.Clock />} />;
        }
        if (value === 'MANUAL_REVIEW') {
          return <Table.ChipCell value="Manual review" icon={<Icon.User />} />;
        }
        if (value === 'VERIFIED') {
          return <Table.ChipCell value="Verified" type="success" icon={<Icon.CircleCheck />} />;
        }
        if (value === 'DENIED') {
          return <Table.ChipCell value="Denied" type="danger" icon={<Icon.CircleCross />} />;
        }
        return <Table.TextCell value="" />;
      },
    },
  ];

  const entityName = getEntityName(rootEntity ?? undefined);
  const entityIcon = getEntityIcon(rootEntity ?? undefined);

  return (
    <>
      <PageHeader text="Entities" border={false}>
        <ButtonGroupDropdown
          buttons={[
            {
              label: 'Create Entity',
              icon: <Icon.Plus />,
              onClick: () => navigate(`${ROUTE.ENTITIES}/edit/person`),
            },
          ]}
          options={[
            {
              label: 'Person',
              icon: <Icon.User />,
              onClick: () => navigate(`${ROUTE.ENTITIES}/edit/person`),
            },
            {
              label: 'Business',
              icon: <Icon.Business />,
              onClick: () => navigate(`${ROUTE.ENTITIES}/edit/business`),
            },
          ]}
        />
      </PageHeader>

      <Loading isLoading={isRootEntityLoading}>
        {rootEntity && (
          <RootEntityCardWrapper px="var(--page-margin)" py={0}>
            <RootEntityLabel>Root Entity</RootEntityLabel>
            <RootEntityCard>
              <ObjectCard
                error={rootEntityError ? 'Failed to load root entity' : undefined}
                icon={entityIcon}
                isLoading={isRootEntityLoading}
                primaryText={entityName || 'Unnamed Entity'}
                id={{ label: 'ID:', value: rootEntity.id }}
                onClick={() =>
                  navigate(isSandbox ? ROUTE.ENTITIES_ROOT_ENTITY_SANDBOX : ROUTE.ENTITIES_ROOT_ENTITY_LIVE)
                }
              />
            </RootEntityCard>
          </RootEntityCardWrapper>
        )}

        <Toolbar>
          <ToolbarGroup>
            {isSmallViewport ? (
              <>
                <Button size="small" variant="secondary" icon={<Icon.Filter />} onClick={() => setIsFiltersOpen(true)}>
                  Filters
                </Button>
                {isFilterActive && (
                  <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                    Reset
                  </ResetFilter>
                )}
              </>
            ) : (
              <>
                <CompactFilterDropdown
                  size="small"
                  active={filters.type}
                  $isFilterActive={Boolean(filters.type)}
                  options={TYPE_OPTIONS}
                  label={TYPE_OPTIONS.find((o) => o.value === filters.type)?.label ?? 'Type'}
                  onChange={(value: EntityType) => updateFilter('type', value)}
                />
                <CompactFilterDropdown
                  size="small"
                  active={filters.verificationStatus}
                  $isFilterActive={Boolean(filters.verificationStatus)}
                  options={VERIFICATION_STATUS_OPTIONS}
                  label={
                    VERIFICATION_STATUS_OPTIONS.find((o) => o.value === filters.verificationStatus)?.label ?? 'Status'
                  }
                  onChange={(value: VerificationStatusFilter) => updateFilter('verificationStatus', value)}
                />
                {isEntitySearchEnabled && (
                  <EntitySearch
                    value={queryInput}
                    placeholder="Search entities..."
                    onValueChange={handleQueryInputChange}
                    onFilterSubmit={(value) => updateFilter('query', value)}
                  />
                )}
                {isFilterActive && (
                  <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                    Reset
                  </ResetFilter>
                )}
              </>
            )}
          </ToolbarGroup>
        </Toolbar>

        <Inner pt={0} px="var(--page-margin)">
          {!isLoading && entities.length === 0 && !isFilterActive ? (
            <EmptyState headline="No entities found" text="Add your first entity to get started.">
              <Button onClick={() => navigate(`${ROUTE.ENTITIES}/edit/person`)} size="small" icon={<Icon.Plus />}>
                Create Entity
              </Button>
            </EmptyState>
          ) : (
            <Table<Entity>
              columns={columns}
              data={entities}
              isLoading={isLoading || isFetching}
              variant={TableVariant.List}
              pagination={{
                hasNext: hasMore,
                hasPrevious,
                pageIndex,
                pageSize,
                onNext: () => {
                  if (entities.length > 0) {
                    onNext({ cursor: entities[entities.length - 1].id });
                  }
                },
                onPrevious,
                onPageSizeChange: setPageSize,
              }}
              onRowClick={(row) => {
                navigate(`${ROUTE.ENTITIES}/edit/${(row.type ?? 'PERSON').toLowerCase()}/${row.id}`);
              }}
              rowAction={(row) => (
                <StyledDropdown
                  isInside
                  positionRight
                  options={[
                    {
                      label: 'Edit',
                      onClick: (event: React.SyntheticEvent) => {
                        event.stopPropagation();
                        navigate(`${ROUTE.ENTITIES}/edit/${(row.type ?? 'PERSON').toLowerCase()}/${row.id}`);
                      },
                    },
                    {
                      label: 'Delete',
                      isDanger: true,
                      onClick: (event: React.SyntheticEvent) => {
                        event.stopPropagation();
                        handleDelete(row);
                      },
                    },
                  ]}
                  variant="dots"
                />
              )}
            />
          )}
        </Inner>
      </Loading>

      <EntityFiltersModal
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        showSearch={isEntitySearchEnabled}
        initialType={filters.type}
        initialVerificationStatus={filters.verificationStatus}
        initialQuery={filters.query}
        onApply={(values) => {
          setFilters((prev) => ({
            ...prev,
            type: values.type,
            verificationStatus: values.verificationStatus,
            query: values.query,
          }));
          setQueryInput(values.query);
        }}
      />
    </>
  );
};
